import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { previewImage, thingsApi, THING_CATEGORIES, type ThingCategory } from '../api/library.js';
import { SelectField } from '../components/Fields.js';
import { EmptyState } from '../components/EmptyState.js';
import { useI18n } from '../i18n/context.js';
import { LibraryCard } from '../library/LibraryCard.js';
import { ActionError } from '../library/ActionError.js';
import { useActionFeedback } from '../library/useActionFeedback.js';
import { LibraryPicker } from '../library/LibraryPicker.js';
import { SimpleEditor } from '../library/SimpleEditor.js';
import { useCreatedItemScroll } from '../library/useCreatedItemScroll.js';

/** Библиотека любимых вещей `/app/things` (01-SPEC.md §5.10). */
export function ThingsPage(): JSX.Element {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookId');
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const action = useActionFeedback();
  const things = useQuery({ queryKey: ['things'], queryFn: () => thingsApi.list() });
  const { scrollToCreatedItem, itemRef } = useCreatedItemScroll<HTMLLIElement>(
    things.data?.items.map((thing) => thing.id) ?? [],
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['things'] });
  };

  if (things.isPending) return <p>{t.common.loading}</p>;
  if (things.isError) return <p role="alert">{t.common.unexpectedError}</p>;

  const items = things.data.items;

  return (
    <section className="mx-auto grid max-w-7xl gap-5">
      <Link
        to={bookId ? `/app/books/${encodeURIComponent(bookId)}?step=things` : '/app'}
        className="kitup-quiet-button w-fit"
      >
        ← {t.common.back}
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">{t.things.title}</h1>
      </div>

      <LibraryPicker
        addLabel={t.things.add}
        createLabel={t.things.create}
        nameLabel={t.things.fields.name}
        options={[]}
        onAdd={() => undefined}
        onCreate={(name) =>
          thingsApi.create({ name, category: 'toy' }).then((created) => {
            setOpenId(created.id);
            scrollToCreatedItem(created.id);
            refresh();
          })
        }
      />
      <ActionError message={action.error} />

      {items.length === 0 ? <EmptyState title={t.things.empty} hint={t.things.emptyHint} /> : null}

      <ul className="grid gap-3">
        {items.map((thing) => (
          <LibraryCard
            key={thing.id}
            elementRef={itemRef(thing.id)}
            title={thing.name}
            subtitle={t.things.categories[thing.category]}
            previewUrl={previewImage(thing)}
            usage={thing.usage}
            renditions={thing.renditions}
            open={openId === thing.id}
            blocked={thing.usage.activeBooks > 0}
            onToggle={() => setOpenId(openId === thing.id ? null : thing.id)}
            onArchive={() => action.run(thingsApi.archive(thing.id).then(refresh))}
            onDelete={() => action.run(thingsApi.remove(thing.id).then(refresh))}
          >
            <SimpleEditor
              entity={thing}
              api={thingsApi}
              styleId={null}
              uploadKind="thing_ref"
              maxPhotos={3}
              photoHint={t.library.photos.hintThing}
              showPrompt={false}
              labels={{ name: t.things.fields.name, description: t.things.fields.description }}
              kindField={
                <SelectField
                  label={t.things.fields.category}
                  value={thing.category}
                  options={THING_CATEGORIES.map((category) => ({
                    value: category,
                    label: t.things.categories[category],
                  }))}
                  onChange={(category: ThingCategory) =>
                    action.run(thingsApi.update(thing.id, { category }).then(refresh))
                  }
                />
              }
              onChanged={refresh}
            />
          </LibraryCard>
        ))}
      </ul>
    </section>
  );
}
